import { useState } from "react";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { CONTACT } from "../data/content";
import { SectionHeader } from "./Home";
import { WhatsAppIcon } from "../components/SiteLayout";

const INITIAL = { full_name: "", email: "", phone: "", company: "", subject: "", message: "" };

export default function ContactPage() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSubmitted(true);
      toast.success("Your message has been sent. We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-20">
          <div>
            <SectionHeader
              eyebrow="Contact Us"
              title="Speak with a property expert."
              sub="Questions about a property in Maharashtra, a pending deal, or our process? Reach out — we respond within one business day."
            />
            <div className="mt-8 flex flex-wrap gap-2" data-testid="contact-reason-chips">
              {["Talk to an expert", "Partnership inquiry", "Agent plan inquiry", "Legal / professional collaboration"].map((r) => (
                <button
                  key={r}
                  type="button"
                  data-testid={`contact-reason-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => setForm((f) => ({ ...f, subject: r }))}
                  className={`text-[0.7rem] uppercase tracking-[0.12em] font-heading px-4 py-2 border transition-colors ${form.subject === r ? "bg-[#113835] text-[#F4D387] border-[#113835]" : "border-[#113835]/20 text-[#1D504C] hover:border-[#C1A74A]"}`}
                >
                  {r}
                </button>
              ))}
            </div>
            <div className="mt-12 space-y-8">
              <div className="flex items-start gap-5">
                <div className="w-12 h-12 border border-[#C1A74A]/50 flex items-center justify-center shrink-0">
                  <Phone size={20} strokeWidth={1.25} className="text-[#C1A74A]" />
                </div>
                <div>
                  <p className="tt-label">Phone</p>
                  <a href={`tel:${CONTACT.phoneRaw}`} data-testid="contact-phone" className="font-heading text-lg text-[#113835] hover:text-[#C1A74A] transition-colors">{CONTACT.phone}</a>
                </div>
              </div>
              <div className="flex items-start gap-5">
                <div className="w-12 h-12 border border-[#C1A74A]/50 flex items-center justify-center shrink-0">
                  <Mail size={20} strokeWidth={1.25} className="text-[#C1A74A]" />
                </div>
                <div>
                  <p className="tt-label">Email</p>
                  <p data-testid="contact-email" className="font-heading text-lg text-[#113835] break-all">{CONTACT.email}</p>
                </div>
              </div>
              <div className="flex items-start gap-5">
                <div className="w-12 h-12 border border-[#C1A74A]/50 flex items-center justify-center shrink-0">
                  <WhatsAppIcon size={20} className="text-[#C1A74A]" />
                </div>
                <div>
                  <p className="tt-label">WhatsApp</p>
                  <a href={`https://wa.me/91${CONTACT.phoneRaw}?text=${encodeURIComponent("Hi Terraa & Title, I need help with a property verification.")}`}
                    target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp"
                    className="font-heading text-lg text-[#113835] hover:text-[#C1A74A] transition-colors">
                    Message us — we reply fast
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-5">
                <div className="w-12 h-12 border border-[#C1A74A]/50 flex items-center justify-center shrink-0">
                  <MapPin size={20} strokeWidth={1.25} className="text-[#C1A74A]" />
                </div>
                <div>
                  <p className="tt-label">Office</p>
                  <p data-testid="contact-address" className="font-heading text-lg text-[#113835]">{CONTACT.address}</p>
                </div>
              </div>
            </div>
            <div className="mt-12 border border-[#113835]/10">
              <iframe
                data-testid="contact-map"
                title="Terraa & Title office location"
                src={`https://www.google.com/maps?q=${encodeURIComponent(CONTACT.mapQuery)}&output=embed`}
                className="w-full h-80"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <div className="bg-[#F9FAFA] border border-[#113835]/10 p-10 lg:p-14 h-fit">
            {submitted ? (
              <div data-testid="contact-success" className="text-center py-20">
                <div className="w-16 h-16 bg-[#113835] text-[#C1A74A] flex items-center justify-center mx-auto mb-8">
                  <Send size={24} strokeWidth={1.25} />
                </div>
                <h2 className="font-heading text-2xl font-medium text-[#113835]">Message received.</h2>
                <p className="mt-4 text-sm text-gray-600 font-light">Thank you for reaching out. Our team will respond within one business day.</p>
                <button data-testid="contact-send-another" onClick={() => { setForm(INITIAL); setSubmitted(false); }} className="tt-btn-outline mt-10">
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-8">
                <h2 className="font-heading text-2xl font-medium text-[#113835]">Send us a message</h2>
                <div className="grid sm:grid-cols-2 gap-8">
                  <div>
                    <label className="tt-label" htmlFor="c-name">Full Name *</label>
                    <input id="c-name" data-testid="contact-input-name" className="tt-input" required value={form.full_name} onChange={set("full_name")} placeholder="Your name" />
                  </div>
                  <div>
                    <label className="tt-label" htmlFor="c-email">Email Address *</label>
                    <input id="c-email" data-testid="contact-input-email" className="tt-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com" />
                  </div>
                  <div>
                    <label className="tt-label" htmlFor="c-phone">Phone Number *</label>
                    <input id="c-phone" data-testid="contact-input-phone" className="tt-input" required value={form.phone} onChange={set("phone")} placeholder="+91 ..." />
                  </div>
                  <div>
                    <label className="tt-label" htmlFor="c-company">Company / Organization</label>
                    <input id="c-company" data-testid="contact-input-company" className="tt-input" value={form.company} onChange={set("company")} placeholder="Optional" />
                  </div>
                </div>
                <div>
                  <label className="tt-label" htmlFor="c-subject">Subject *</label>
                  <input id="c-subject" data-testid="contact-input-subject" className="tt-input" required value={form.subject} onChange={set("subject")} placeholder="How can we help?" />
                </div>
                <div>
                  <label className="tt-label" htmlFor="c-message">Message *</label>
                  <textarea id="c-message" data-testid="contact-input-message" className="tt-input" required value={form.message} onChange={set("message")} placeholder="Tell us about your requirement..." />
                </div>
                <button data-testid="contact-submit-button" type="submit" disabled={submitting} className="tt-btn-primary w-full justify-center disabled:opacity-60">
                  {submitting ? "Sending..." : "Send Message"} <Send size={15} strokeWidth={1.5} />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
